import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import PageShell from "@/components/layout/PageShell";

// Countries whose intake form is live (forms/<code>.html). Canada and Portugal have their own pages.
export const LIVE_FORMS = ["usa", "mexico", "brazil", "panama", "france", "uk", "romania", "italy", "netherlands", "ireland", "estonia", "belgium", "germany"];
const COUNTRIES = [
  { code: "canada", flag: "🇨🇦", name: "Canada", to: "/apply/canada", active: true },
  { code: "usa", flag: "🇺🇸", name: "USA", active: false },
  { code: "mexico", flag: "🇲🇽", name: "Mexico", active: false },
  { code: "brazil", flag: "🇧🇷", name: "Brasil", active: false },
  { code: "panama", flag: "🇵🇦", name: "Panama", active: false },
  { code: "portugal", flag: "🇵🇹", name: "Portugal", to: "/apply/portugal", active: true },
  { code: "france", flag: "🇫🇷", name: "France", active: false },
  { code: "uk", flag: "🇬🇧", name: "United Kingdom", active: false },
  { code: "romania", flag: "🇷🇴", name: "Romania", active: false },
  { code: "italy", flag: "🇮🇹", name: "Italy", active: false },
  { code: "netherlands", flag: "🇳🇱", name: "Netherlands", active: false },
  { code: "ireland", flag: "🇮🇪", name: "Ireland", active: false },
  { code: "estonia", flag: "🇪🇪", name: "Estonia", active: false },
  { code: "belgium", flag: "🇧🇪", name: "Belgium", active: false },
  { code: "germany", flag: "🇩🇪", name: "Germany", active: false },
].map((c) => (LIVE_FORMS.includes(c.code) ? { ...c, to: `/apply/${c.code}`, active: true } : c));

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] } }),
};

export const VisaCountriesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto" data-testid="visa-countries-page">
        <Link to="/apply" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6" data-testid="visa-back">
          <ArrowLeft className="h-4 w-4" /> {t("visaCountries.back")}
        </Link>
        <div className="mb-8">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-2">
            {t("common.nav.visa")}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-brand-gradient leading-tight">{t("visaCountries.title")}</h1>
          <p className="text-slate-300 mt-2 max-w-2xl">{t("visaCountries.subtitle")}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {COUNTRIES.map((c, i) => (
            <motion.button
              key={c.code}
              type="button"
              disabled={!c.active}
              onClick={() => c.active && navigate(c.to)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              variants={fade}
              custom={i}
              className={`text-left rounded-2xl border p-5 transition-colors ${
                c.active
                  ? "border-venka-green/40 bg-card hover:border-venka-green cursor-pointer"
                  : "border-white/10 bg-card/50 opacity-70 cursor-not-allowed"
              }`}
              data-testid={`visa-country-${c.code}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-3xl leading-none">{c.flag}</span>
                {c.active ? (
                  <span className="rounded-full bg-venka-green/20 text-venka-green-light text-[11px] font-semibold px-2.5 py-0.5">
                    {t("visaCountries.start")}
                  </span>
                ) : (
                  <span className="rounded-full border border-white/15 text-slate-400 text-[11px] px-2.5 py-0.5">
                    {t("visaCountries.comingSoon")}
                  </span>
                )}
              </div>
              <h3 className="mt-3 font-serif text-lg text-white flex items-center gap-2">
                <MapPin className="h-4 w-4 text-venka-green-light" /> {c.name}
              </h3>
              {c.active && (
                <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-venka-green-light">
                  {t("visaCountries.start")} <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    </PageShell>
  );
};

export default VisaCountriesPage;
